import { useEffect, useState, type ReactNode } from 'react';
import { BERICHTEN_BEWAAR_DAGEN, isVasteKamer } from '@tolk/shared';
import { ApiFout, leesVerslag, meldingVoor, type Verslag, type VerslagBericht } from '../lib/api';
import { alsRegels } from '../lib/regels';
import {
  bestandsnaam,
  datumVolledig,
  klok,
  nootZinnen,
  taalLabel,
  taalnaam,
  tijdspanne,
  tijdstip,
  tijdzoneNaam,
  vertaalcel,
} from '../lib/verslag';
import { isRechtsNaarLinks, teksten } from '../teksten';
import type { Teksten } from '../teksten/nl';

/**
 * Het gespreksverslag: wie wat zei, origineel en vertaling naast elkaar.
 *
 * BESLOTEN OP 23 SEPTEMBER 2026, na een mockup:
 *   - Alleen de beheerder haalt het op. De gegevens komen van achter de inlog
 *     (/api/beheer/...); deze pagina zelf is gewoon een pagina van de client.
 *   - Koppen in DE TWEE TALEN VAN HET GESPREK. De beheerder geeft het verslag
 *     aan de ander, en die moet het ook kunnen lezen.
 *   - De BROWSER maakt de PDF, met "Afdrukken" en dan "Opslaan als PDF". Geen
 *     PDF-bibliotheek en geen lettertypen op de server: Arabisch, Chinees,
 *     Japans en Koreaans staan al op het toestel van de lezer, inclusief de
 *     rechts-naar-links-opmaak die een PDF-bibliotheek zelf zou moeten doen.
 *
 * DE BALK BOVENAAN IS NEDERLANDS en komt niet op papier (print:hidden). Die is
 * voor de beheerder, zoals de rest van het beheer.
 */

/** Tekst in de TWEEDE taal, met eigen taal en richting, naast tekst in de eerste. */
function Tweede({ taal, children }: { taal: string; children: ReactNode }) {
  return (
    <bdi lang={taal} dir={isRechtsNaarLinks(taal) ? 'rtl' : 'ltr'}>
      {children}
    </bdi>
  );
}

/** Een tekstcel: klein taallabel erboven, daarna de zinnen op eigen regels. */
function Tekstcel({ taal, tekst, vertaling }: { taal: string; tekst: string; vertaling?: boolean }) {
  const rtl = isRechtsNaarLinks(taal);
  return (
    <td
      lang={taal}
      dir={rtl ? 'rtl' : 'ltr'}
      className={`px-2 py-2 align-top border-b border-slate-250 ${vertaling === true ? 'text-gio-dark' : ''}
                  ${rtl ? 'text-[11.5pt] leading-[1.7]' : ''}`}
    >
      <span className="block text-[7pt] uppercase tracking-wide text-slate-450 mb-0.5">{taalLabel(taal)}</span>
      {/* Een zin per regel, zoals in het gesprek; alleen de aanhef niet apart. */}
      {alsRegels(tekst, { aanhefApart: false }).map((regel, i) => (
        <span key={`${i}-${regel}`} className="block">
          {regel}
        </span>
      ))}
    </td>
  );
}

/** Een ontbrekende vertaling. Oranje stippellijn: nooit een lege cel. */
function Ontbreekt({ a, b, taalB }: { a: string; b: string; taalB: string }) {
  return (
    <td className="px-2 py-2 align-top border-b border-slate-250">
      <span className="block px-1.5 py-1 border border-dashed border-gio-orange-deep rounded text-[8pt] italic text-[#8A4F00]">
        {a}
        <br />
        <Tweede taal={taalB}>{b}</Tweede>
      </span>
    </td>
  );
}

function Microfoon() {
  return (
    <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" className="w-2.5 h-2.5 shrink-0">
      <path d="M8 1a2.5 2.5 0 0 0-2.5 2.5v4a2.5 2.5 0 0 0 5 0v-4A2.5 2.5 0 0 0 8 1Zm-5 6.5h1.2a3.8 3.8 0 0 0 7.6 0H13a5 5 0 0 1-4.4 5V15H7.4v-2.5A5 5 0 0 1 3 7.5Z" />
    </svg>
  );
}

function Toetsenbord() {
  return (
    <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" className="w-2.5 h-2.5 shrink-0">
      <path d="M1.5 4h13a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-13a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Zm1 1.8v1.4h1.4V5.8Zm2.4 0v1.4h1.4V5.8Zm2.4 0v1.4h1.4V5.8Zm2.4 0v1.4h1.4V5.8Zm2.4 0v1.4h1.4V5.8ZM4.5 9v1.3h7V9Z" />
    </svg>
  );
}

function Regel({ bericht, tA, tB, taalA, taalB }: {
  bericht: VerslagBericht;
  tA: Teksten;
  tB: Teksten;
  taalA: string;
  taalB: string;
}) {
  const cel = vertaalcel(bericht);
  const b = bericht.senderSlot === 'b';
  return (
    <tr className={`break-inside-avoid ${b ? 'bg-[#F7F9FB]' : ''}`}>
      <td className="px-2 py-2 align-top border-b border-slate-250">
        <span
          className={`inline-block px-1.5 rounded text-white font-bold text-[8pt] ${b ? 'bg-gio-dark' : 'bg-gio-blue'}`}
        >
          {b ? 'B' : 'A'}
        </span>
        <span className="block mt-0.5 text-[8pt] font-semibold text-gio-dark">{klok(bericht.createdAt, taalA)}</span>
        <span className="flex items-center gap-1 mt-0.5 text-[7pt] text-slate-550">
          {bericht.ingesproken ? <Microfoon /> : <Toetsenbord />}
          {bericht.ingesproken ? tA.verslagGesproken : tA.verslagGetypt}
        </span>
        <span className="block text-[7pt] text-slate-450">
          <Tweede taal={taalB}>{bericht.ingesproken ? tB.verslagGesproken : tB.verslagGetypt}</Tweede>
        </span>
      </td>

      <Tekstcel taal={bericht.srcLang} tekst={bericht.originalText} />

      {cel.soort === 'tekst' && <Tekstcel taal={bericht.dstLang} tekst={cel.tekst} vertaling />}
      {cel.soort === 'mislukt' && <Ontbreekt a={tA.verslagMislukt} b={tB.verslagMislukt} taalB={taalB} />}
      {cel.soort === 'niet_klaar' && <Ontbreekt a={tA.verslagNietKlaar} b={tB.verslagNietKlaar} taalB={taalB} />}
    </tr>
  );
}

/** Een kopje in beide talen: "Origineel · Eredeti". */
function Kop({ a, b, taalB }: { a: string; b: string; taalB: string }) {
  return (
    <>
      {a} <span className="font-normal opacity-80">· <Tweede taal={taalB}>{b}</Tweede></span>
    </>
  );
}

/** Een blok met de voetnoot of de eerlijkheidsnoot, in een taal. */
function Alinea({ taal, zinnen }: { taal: string; zinnen: string[] }) {
  const [eerste, ...rest] = zinnen;
  const rtl = isRechtsNaarLinks(taal);
  // Arabisch schrift is bij dezelfde puntgrootte kleiner dan het latijnse, en
  // de voetnoot staat al op 7,5 punt. Gemeten in de PDF van 23 september 2026.
  return (
    <p lang={taal} dir={rtl ? 'rtl' : 'ltr'} className={`m-0 ${rtl ? 'text-[1.2em] leading-[1.6]' : ''}`}>
      <b className="text-gio-dark">{eerste}</b> {rest.join(' ')}
    </p>
  );
}

function Document({ verslag }: { verslag: Verslag }) {
  const { kamer, berichten } = verslag;
  const taalA = kamer.langA;
  const taalB = kamer.langB;
  const tA = teksten(taalA);
  const tB = teksten(taalB);
  const { begin, eind } = tijdspanne(verslag);
  const zone = tijdzoneNaam(begin, taalA);

  const voet = (t: Teksten, taal: string): string[] => [
    t.verslagVertrouwelijk,
    t.verslagAlleenDeelnemers,
    // Een vaste kamer wordt nooit als geheel gewist; zijn berichten wel, elk
    // na dertig dagen. Een datum in het jaar 9999 zou daarover liegen.
    isVasteKamer(kamer.expiresAt)
      ? t.verslagGewistVast
      : t.verslagGewist.replace('{tijd}', tijdstip(verslag.gewistVanaf, taal)),
  ];

  return (
    <article
      className="verslag bg-white text-slate-800 text-[10pt] leading-[1.45] w-full max-w-[210mm] mx-auto
                 p-[12mm] shadow-[0_2px_12px_rgba(16,57,84,.18)]
                 print:max-w-none print:p-0 print:shadow-none"
    >
      <header className="flex items-center gap-3 pb-2.5 border-b-[3px] border-gio-blue">
        <img src="/askgio-avatar.png" alt="Ask Gio" className="w-10 h-10 rounded-full object-cover shrink-0" />
        <div className="flex-1 min-w-0">
          <h1 className="m-0 text-[16pt] leading-tight font-extrabold text-gio-dark">
            {tA.verslagTitel}
            <span className="block text-[12pt] font-medium text-gio-blue">
              <Tweede taal={taalB}>{tB.verslagTitel}</Tweede>
            </span>
          </h1>
          <div className="text-[8.5pt] text-slate-550">
            Ask Gio Tolk{kamer.label.trim() !== '' && <> · {kamer.label}</>}
          </div>
        </div>
        <div className="text-right text-[9pt] text-gio-dark shrink-0">
          <strong className="block text-[11pt]">{datumVolledig(begin, taalA)}</strong>
          <Tweede taal={taalB}>{datumVolledig(begin, taalB)}</Tweede>
          <br />
          {klok(begin, taalA)} – {klok(eind, taalA)}
          {zone !== '' && <> ({zone})</>}
        </div>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 print:grid-cols-4 gap-2 my-2.5 text-[8.5pt]">
        {(['a', 'b'] as const).map((slot) => {
          const x = slot.toUpperCase();
          const code = slot === 'a' ? taalA : taalB;
          return (
            <div key={slot} className="bg-slate-150 rounded px-2 py-1.5">
              <span className="block text-[7.5pt] text-slate-550">
                <Kop a={tA.verslagSpreker.replace('{x}', x)} b={tB.verslagSpreker.replace('{x}', x)} taalB={taalB} />
              </span>
              <b className="text-gio-dark">{taalnaam(code, taalA)}</b> ·{' '}
              <Tweede taal={taalB}>{taalnaam(code, taalB)}</Tweede>
            </div>
          );
        })}
        <div className="bg-slate-150 rounded px-2 py-1.5">
          <span className="block text-[7.5pt] text-slate-550">
            <Kop a={tA.verslagBerichten} b={tB.verslagBerichten} taalB={taalB} />
          </span>
          <b className="text-gio-dark">{berichten.length}</b>
        </div>
        <div className="bg-slate-150 rounded px-2 py-1.5">
          <span className="block text-[7.5pt] text-slate-550">
            <Kop a={tA.verslagGemaaktOp} b={tB.verslagGemaaktOp} taalB={taalB} />
          </span>
          <b className="text-gio-dark">{tijdstip(verslag.gemaaktOp, taalA)}</b>
        </div>
      </div>

      <div
        className="grid grid-cols-1 sm:grid-cols-2 print:grid-cols-2 gap-3 mb-3 px-2.5 py-2 border border-slate-250
                   border-l-4 border-l-gio-orange rounded text-[8pt] text-slate-650"
      >
        <Alinea taal={taalA} zinnen={nootZinnen(verslag, tA)} />
        <Alinea taal={taalB} zinnen={nootZinnen(verslag, tB)} />
      </div>

      <table className="w-full border-collapse table-fixed">
        <colgroup>
          <col className="w-[22mm]" />
          <col />
          <col />
        </colgroup>
        <thead>
          <tr className="bg-gio-dark text-white text-[8pt] text-start">
            <th className="px-2 py-1.5 text-start font-semibold">
              <Kop a={tA.verslagWie} b={tB.verslagWie} taalB={taalB} />
            </th>
            <th className="px-2 py-1.5 text-start font-semibold">
              <Kop a={tA.verslagOrigineel} b={tB.verslagOrigineel} taalB={taalB} />
            </th>
            <th className="px-2 py-1.5 text-start font-semibold">
              <Kop a={tA.verslagVertaling} b={tB.verslagVertaling} taalB={taalB} />
            </th>
          </tr>
        </thead>
        <tbody>
          {berichten.map((bericht) => (
            <Regel key={bericht.id} bericht={bericht} tA={tA} tB={tB} taalA={taalA} taalB={taalB} />
          ))}
          {berichten.length === 0 && (
            <tr>
              <td colSpan={3} className="px-2 py-3 text-center text-slate-450 border-b border-slate-250">
                —
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <footer className="grid grid-cols-1 sm:grid-cols-2 print:grid-cols-2 gap-3 mt-3.5 pt-2 border-t border-slate-250 text-[7.5pt] text-slate-550 break-inside-avoid">
        <Alinea taal={taalA} zinnen={voet(tA, taalA)} />
        <Alinea taal={taalB} zinnen={voet(tB, taalB)} />
      </footer>
    </article>
  );
}

type Stand =
  | { soort: 'laden' }
  | { soort: 'klaar'; verslag: Verslag }
  | { soort: 'fout'; code: string };

export default function VerslagPage({ kamerId }: { kamerId: string }) {
  const [stand, setStand] = useState<Stand>({ soort: 'laden' });

  useEffect(() => {
    let levend = true;
    leesVerslag(kamerId)
      .then((verslag) => {
        if (levend) setStand({ soort: 'klaar', verslag });
      })
      .catch((fout: unknown) => {
        if (levend) setStand({ soort: 'fout', code: fout instanceof ApiFout ? fout.code : 'onbekend' });
      });
    return () => {
      levend = false;
    };
  }, [kamerId]);

  // De titel wordt de bestandsnaam van de PDF. Zie lib/verslag.ts.
  useEffect(() => {
    if (stand.soort === 'klaar') document.title = bestandsnaam(stand.verslag);
  }, [stand]);

  if (stand.soort === 'laden') {
    return <p className="p-6 text-sm text-slate-550">Verslag ophalen…</p>;
  }

  if (stand.soort === 'fout') {
    const tekst =
      stand.code === 'room_not_found'
        ? 'Dit gesprek bestaat niet meer. Een gesprek wordt een uur na het verlopen of sluiten gewist, en daarmee alles wat er gezegd is. Er valt geen verslag meer van te maken.'
        : stand.code === 'niet_ingelogd'
          ? 'Je bent niet ingelogd. Log in op de beginpagina en open het verslag daarna opnieuw.'
          : meldingVoor(stand.code);
    return (
      <div className="max-w-md mx-auto p-6 space-y-3">
        <p className="text-sm text-gio-dark">{tekst}</p>
        <a href="/" className="text-sm font-bold text-gio-blue hover:text-gio-dark">
          Naar de beginpagina
        </a>
      </div>
    );
  }

  const { verslag } = stand;
  const loopt = verslag.kamer.closedAt === null && verslag.kamer.expiresAt > Date.now();

  return (
    <div className="min-h-dvh bg-[#D5DCE4] py-4 px-4 print:bg-white print:p-0">
      {/* Voor de beheerder, niet op papier. */}
      <div
        className="print:hidden max-w-[210mm] mx-auto mb-4 bg-white border-l-4 border-gio-orange rounded-lg p-4
                   space-y-2 text-sm text-slate-650"
      >
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => window.print()}
            className="px-4 py-2 rounded-lg bg-gio-blue hover:bg-gio-dark text-white font-bold
                       transition-colors duration-155"
          >
            Afdrukken of opslaan als PDF
          </button>
          <span className="text-xs text-slate-550">
            Kies in het afdrukvenster bij bestemming voor <b>Opslaan als PDF</b>.
          </span>
        </div>
        {loopt && (
          <p className="text-xs text-gio-orange-deep">
            Dit gesprek loopt nog. Zinnen die nog vertaald worden, staan erin als niet klaar. Maak het
            verslag na afloop opnieuw voor de volledige versie.
          </p>
        )}
        <p className="text-xs">
          {isVasteKamer(verslag.kamer.expiresAt) ? (
            <>
              Vaste kamer: elk bericht wordt <b>{BERICHTEN_BEWAAR_DAGEN} dagen</b> na het versturen gewist.
              Oudere berichten staan er niet meer in.
            </>
          ) : (
            <>
              Het gesprek wordt gewist vanaf <b>{tijdstip(verslag.gewistVanaf, 'nl-NL')}</b>. Daarna kan dit
              verslag niet opnieuw gemaakt worden.
            </>
          )}{' '}
          Dit verslag bevat de inhoud van het gesprek: deel het alleen met de deelnemers.
        </p>
      </div>

      <Document verslag={verslag} />
    </div>
  );
}
